// Leases separate component lifetime from document lifetime. No persisted PDFs.
export function createDocumentPool(open, { maxDocuments = 3, maxBytes = 64 * 1024 * 1024, idleMs = 120000 } = {}) {
    const entries = new Map(), queue = [];
    let owner, running = 0;
    function drop(entry) {
        entry.dead = true;
        clearTimeout(entry.timer);
        if (entries.get(entry.id) === entry) entries.delete(entry.id);
        entry.value?.dispose();
    }
    function trim() {
        let bytes = [...entries.values()].reduce((sum, e) => sum + (e.value?.byteLength || 0), 0);
        for (const entry of entries.values()) {
            if (entries.size <= maxDocuments && bytes <= maxBytes) break;
            if (!entry.refs && entry.value) { bytes -= entry.value.byteLength || 0; drop(entry); }
        }
    }
    function finishOpening() {
        running--;
        pump();
    }
    function pump() {
        while (running < 2 && queue.length) {
            queue.sort((a, b) => b.priority - a.priority);
            const entry = queue.shift();
            if (entry.dead) { entry.reject(new Error('Preview closed')); continue; }
            running++;
            Promise.resolve().then(() => open(entry.id, entry.owner)).then(value => {
                if (entry.dead) { value.dispose(); throw new Error('Preview closed'); }
                entry.value = value;
                entry.resolve(value);
                trim();
            }).catch(error => { drop(entry); entry.reject(error); }).finally(finishOpening);
        }
    }
    function clear() { for (const entry of entries.values()) drop(entry); }
    return {
        acquire(id, token, priority = 1) {
            if (owner !== token) { clear(); owner = token; }
            let entry = entries.get(id);
            if (!entry) {
                entry = { id, owner: token, refs: 0, priority, dead: false };
                entry.promise = new Promise((resolve, reject) => { entry.resolve = resolve; entry.reject = reject; });
                entries.set(id, entry); queue.push(entry);
            }
            entry.refs++; entry.priority = Math.max(entry.priority, priority);
            clearTimeout(entry.timer);
            entries.delete(id); entries.set(id, entry); // LRU.
            pump();
            let released = false;
            return { promise: entry.promise, release() {
                if (released) return;
                released = true; entry.refs--;
                if (!entry.refs && !entry.dead) {
                    if (entry.dieAfterRelease) { drop(entry); return; }
                    entry.value?.pause?.();
                    entry.timer = setTimeout(() => drop(entry), idleMs);
                    trim();
                }
            } };
        },
        // A document left the order (not just a tab switch): its PbReader
        // session/PDF must not linger for `idleMs` — it competes with the
        // remaining documents' preflight requests for the same worker.
        forget(id) {
            const entry = entries.get(id);
            if (!entry) return;
            if (entry.refs) entry.dieAfterRelease = true;
            else drop(entry);
        },
        clear,
    };
}
